/* eslint-disable react/prop-types */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { useWindowWidth } from '@nike/nike-design-system-components';
import { css } from '@emotion/react';

import {
  requestReturns,
  updateReturnsSortBy,
  setReturnsFilters,
  clearReturnsSortBy,
  clearReturnsFilters,
  clearReturnsFilter,
  updateReturnsDateRange,
  setReturnsListType,
  setSearchValues,
  addReturnsFilter,
  removeReturnsFilter,
  setRequestAnchor,
  setNavNewReturn,
  deleteReturn
} from './actions';
import {
  getReturnListV2,
  getTotalResources,
  getIsFetching,
  getRequestAnchor,
  getReturnsListType,
  getFilterOptions,
  getSelectedFilters,
  getSearchValues,
  getSortBy,
  getDateRange
} from './reducer';

import { getReturnStatusColumns } from './columnsV2';

import { setSelectedReturnId } from '../orderReturns/actions';
import { closeModal, openModal } from '../../components/modal/actions';
import { getUserAccount, selectDepartments } from '../../user/selectors';
import { getIsGreaterChinaSalesOrg, getIsMANASalesOrg } from '../../gamedayAccount/selectors';
import { userDepartments } from '../../user/reducer';

import {
  RETURN_AUTH_ID_KEY,
  DEPT_NAMES_KEY,
  CREDIT_TYPE_KEY,
  RETURN_STATUS_KEY,
  ALL_RETURNS,
  UNSUBMITTED_RETURNS,
  DEFAULT_SORT,
  RETURN_CREDIT_OPTIONS,
  filterKeyMap
} from './constants';
import { DEFAULT_DATE_RANGE, LABEL_SELECT_ALL, RETURN_CODE_UNSUBMITTED } from '../../shop/constants';
import { RETURN_STATUS_OPTIONS, MANA_RETURN_STATUS_OPTIONS } from '../orderReturns/constants';
import { searchKeyOptions, sortOptions, dateRangeOptions } from './selectOptions';

import { mapDepartmentNumbers } from '../status/util';
import { subtractDateTime } from '../../util/date';

import PageHeader from '../../components/containers/PageHeader';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { Button, Link, NoResults } from '../../components/';
import { StatusSearchV2 } from '../../components/formInputs';
import SortOptions from '../../components/sortOptions/SortOptions';
import { Filters } from '../../shop/filters/';
import DateRangeSelector from '../DateRangeSelector';
import LoadingMessage from '../../components/spinner/LoadingMessage';
import TableInfiniteScroll from '../../components/table/TableInfiniteScroll';
import ReturnStatusCard from './ReturnStatusCard';

const ReturnStatus = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobile = useWindowWidth() < 960;

  const returnList = useSelector(getReturnListV2);
  const totalResources = useSelector(getTotalResources);
  const isFetching = useSelector(getIsFetching);
  const requestAnchor = useSelector(getRequestAnchor);
  const returnsListType = useSelector(getReturnsListType);
  const filterOptions = useSelector(getFilterOptions);
  const selectedFilters = useSelector(getSelectedFilters);
  const { searchKey, searchTerm } = useSelector(getSearchValues);
  const sortBy = useSelector(getSortBy);
  const dateRange = useSelector(getDateRange);

  const userAccount = useSelector(getUserAccount);
  const userDepts = useSelector(userDepartments);
  const accountDepts = useSelector(selectDepartments)?.find(dept => dept.customerNumber === userAccount.number)
    ?.customerDepartments;
  const isGreaterChinaSalesOrg = useSelector(getIsGreaterChinaSalesOrg);
  const isMANASalesOrg = useSelector(getIsMANASalesOrg);

  const hasSelectedFilters = Object.values(Object.fromEntries(selectedFilters)).some(filter => filter.size);
  const hasAnyFilterOrSort =
    searchTerm !== '' || sortBy?.field !== 'createTimestamp' || sortBy?.order !== 'DESC' || hasSelectedFilters;

  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const memoizedColumns = useMemo(() => getReturnStatusColumns(accountDepts, returnsListType === UNSUBMITTED_RETURNS), [
    returnsListType
  ]);

  const hasMoreReturnsToFetch = returnList.length < totalResources;
  const deptSelectAll = !selectedFilters.get(DEPT_NAMES_KEY).size;

  // retrieving departments and setting MANA return status filter options
  useEffect(() => {
    const returnsFilters = [
      { key: DEPT_NAMES_KEY, values: userDepts.deptNames },
      {
        key: CREDIT_TYPE_KEY,
        values: RETURN_CREDIT_OPTIONS.map(opt => opt.split('-').join('.').toLowerCase())
      }
    ];
    if (isMANASalesOrg) {
      returnsFilters.push({ key: RETURN_STATUS_KEY, values: MANA_RETURN_STATUS_OPTIONS });
    } else {
      returnsFilters.push({ key: RETURN_STATUS_KEY, values: RETURN_STATUS_OPTIONS });
    }
    dispatch(setReturnsFilters(returnsFilters));
  }, [userAccount, isMANASalesOrg]);

  useEffect(() => {
    const departmentNames = Array.from(selectedFilters.get(DEPT_NAMES_KEY));
    const creditTypes = Array.from(selectedFilters.get('creditType'));
    const returnStatus = Array.from(selectedFilters.get('returnStatus'));

    let filterParams = `filter=compAccountNumber(${
      userAccount.number
    })&filter=expirationDateAfterEq(${subtractDateTime({ dateTime: new Date().toISOString(), months: '24' })})`;
    if (departmentNames.length) {
      filterParams += `&filter=departmentNumber(${mapDepartmentNumbers(departmentNames, userDepts.depts).join(',')})`;
    }
    if (creditTypes.length) {
      filterParams += `&filter=creditType(${creditTypes.map(type => type.split('.').join('-').toUpperCase())})`;
    }
    if (returnsListType === UNSUBMITTED_RETURNS) {
      filterParams += `&filter=status(${UNSUBMITTED_RETURNS.toUpperCase()})`;
    } else if (returnStatus.length) {
      filterParams += `&filter=status(${returnStatus.join(',').toUpperCase()})`;
    } else if (isMANASalesOrg) {
      filterParams += `&filter=status(${MANA_RETURN_STATUS_OPTIONS.join(',')})`;
    } else {
      filterParams += `&filter=status(${RETURN_STATUS_OPTIONS.join(',')})`;
    }

    if (searchTerm && searchKey) {
      filterParams += `&filter=${filterKeyMap[searchKey]}Like(${searchTerm})`;
    }

    if (dateRange.period === 'custom' && dateRange.startDate && dateRange.endDate) {
      filterParams += `&filter=createTimestampAfterEq(${new Date(
        dateRange.startDate
      ).toISOString()})&filter=createTimestampBeforeEq(${new Date(dateRange.endDate).toISOString()})`;
    } else if (dateRange.period !== 'custom') {
      filterParams += `&filter=createTimestampAfterEq(${new Date(
        subtractDateTime({ dateTime: new Date().toISOString(), months: dateRange.period.split(' ')[0] })
      ).toISOString()})`;
    }

    const sortParams = `&sort=${sortBy.field}${sortBy.order.charAt(0) + sortBy.order.slice(1).toLowerCase()}`;

    dispatch(requestReturns({ filter: filterParams, sort: sortParams, anchor: requestAnchor, count: 25 }));
  }, [
    searchTerm,
    searchKey,
    returnsListType,
    sortBy,
    selectedFilters,
    JSON.stringify(dateRange),
    requestAnchor,
    userAccount
  ]);

  const handleLoadNextPage = async () => {
    setTimeout(() => {
      if (hasMoreReturnsToFetch && !isFetching) {
        dispatch(setRequestAnchor(returnList.length));
      }
    }, 250);
  };

  const handleSelection = async (name, isChecked) => {
    dispatch(setRequestAnchor(0));

    const [key, value] = name.split('--');
    if (name === LABEL_SELECT_ALL && isChecked) {
      // for department select all, just clear department filter
      await dispatch(clearReturnsFilter(DEPT_NAMES_KEY));
    } else if (isChecked) {
      await dispatch(addReturnsFilter({ key, value }));
    } else {
      await dispatch(removeReturnsFilter({ key, value }));
    }
  };

  const handleListSort = async ({ value, desc }) => {
    dispatch(setRequestAnchor(0));

    const updatedSort = { field: value, order: desc ? 'DESC' : 'ASC' };
    if (sortBy !== updatedSort) {
      dispatch(updateReturnsSortBy(updatedSort));
    }
  };

  const clearSearchAndFilters = () => {
    dispatch(clearReturnsFilters());
    dispatch(clearReturnsSortBy());
    dispatch(updateReturnsDateRange({ period: DEFAULT_DATE_RANGE }));
    dispatch(setSearchValues({ searchKey: RETURN_AUTH_ID_KEY, searchTerm: '' }));
  };

  const handleReturnClick = (selectedReturn, e) => {
    e.preventDefault();
    dispatch(setSelectedReturnId(selectedReturn.id));
    if (selectedReturn.status === RETURN_CODE_UNSUBMITTED) {
      // Unsubmitted Return -> Return Steps
      history.push(`/orders/returns/id/${selectedReturn.orderId}`);
    } else {
      // Submitted Return -> Return Details
      history.push(`/orders/returns/details/id/${selectedReturn.id}`);
    }
  };

  const handleDeleteClick = (returnId, e) => {
    e.stopPropagation();

    dispatch(
      openModal({
        type: 'alert',
        title: new TranslatedCopy({ id: 'returnStatus.modal.delete.title' }).toString(),
        confirmText: new TranslatedCopy({ id: 'global.confirm' }).toString(),
        confirmAction: () =>
          dispatch(deleteReturn({ returnId })).then(() => {
            dispatch(closeModal());
            dispatch(setRequestAnchor(0));
          }),
        cancelText: new TranslatedCopy({ id: 'global.cancel' }).toString(),
        cancelAction: () => dispatch(closeModal())
      })
    );
  };

  const beginReturn = async e => {
    e.preventDefault();
    await dispatch(setNavNewReturn(true));
    history.push('/orders/status');
  };

  return (
    <div className='returnStatus'>
      <div className='status__main_outerWrapper'>
        <PageHeader
          page='returnStatus'
          title={
            <>
              <span className='mr5-lg' data-desc='returnStatus__pageHeader_copy'>
                <TranslatedCopy id='returnStatus.route' />
              </span>
              <div className='d-sm-b d-md-ib'>
                {!isFetching && (
                  <div className='returnStatus__searchResults body-4 mt1-sm'>
                    <div className='mr3-sm' data-desc='returnStatus__search_count'>
                      <TranslatedCopy id='orderstatus.header.result' values={{ amount: totalResources || 0 }} />
                    </div>
                    {hasAnyFilterOrSort && (
                      <Button
                        className='text-color-secondary body-4 u-underline fl-sm-r pt0-sm pb0-sm'
                        data-desc='returnStatus__search_clear'
                        useBareClass
                        textLink
                        onClick={clearSearchAndFilters}
                      >
                        <TranslatedCopy id='orderstatus.search.clear' />
                      </Button>
                    )}
                  </div>
                )}
                <div className='d-sm-ib d-md-h'>
                  <Link
                    to='/'
                    extraClassName='body-4 text-color-secondary ncss-base u-capitalize'
                    data-desc='returnStatus__start_mobile'
                    onClick={beginReturn}
                  >
                    <TranslatedCopy id='global.beginReturn' />
                  </Link>
                </div>
              </div>
              <div>
                <Link
                  to='/'
                  extraClassName='body-4 text-color-secondary ncss-base d-sm-h d-md-ib u-capitalize'
                  data-desc='returnStatus__start_desktop'
                  onClick={beginReturn}
                >
                  <TranslatedCopy id='global.beginReturn' />
                </Link>
              </div>
            </>
          }
          className={classnames('flex-container row bg-white align-center', { hasResults: hasSelectedFilters })}
        >
          <div className={classnames('flex-container center space-between', { row: !isMobile })}>
            <div className='status__main_menu u-full-height flex-container row align-center'>
              <Button
                onClick={() => dispatch(setReturnsListType(ALL_RETURNS))}
                className={classnames('body-3 p0-sm d-sm-ib mr4-sm', {
                  selected: returnsListType === ALL_RETURNS
                })}
              >
                <TranslatedCopy id='returnStatus.header.allReturns' />
              </Button>
              <Button
                onClick={() => dispatch(setReturnsListType(UNSUBMITTED_RETURNS))}
                className={classnames('body-3 p0-sm d-sm-ib', {
                  selected: returnsListType === UNSUBMITTED_RETURNS
                })}
              >
                <TranslatedCopy id='returnStatus.header.unsubmitted' />
              </Button>
            </div>
            <div css={{ padding: isMobile ? '8px' : 0 }}>
              <StatusSearchV2
                name='returnStatus__search'
                clearSearch={() => dispatch(setSearchValues({ searchKey, searchTerm: '' }))}
                searchKeyOptions={searchKeyOptions}
                setSearchValues={value => dispatch(setSearchValues(value))}
              />
            </div>
            {isMobile && (
              <div
                className='flex-container row space-between'
                css={{ width: '100%', alignItems: 'center', padding: '10px' }}
              >
                <Button onClick={() => setIsMobileFiltersOpen(true)}>
                  <TranslatedCopy id='global.filter' />
                </Button>
                <SortOptions
                  list={returnList}
                  sortBy={sortOptions}
                  defaultValue={{
                    value: DEFAULT_SORT.field,
                    name: new TranslatedCopy({ id: 'returnStatus.orderSubmittedDate' }).toString(),
                    desc: true
                  }}
                  handleExternalSort={handleListSort}
                />
              </div>
            )}
          </div>
        </PageHeader>
        <div
          className={classnames('status__filters order-status-item pl8-md pr4-md flex-container', {
            isOpen: isMobileFiltersOpen
          })}
        >
          <Filters
            filters={
              returnsListType === UNSUBMITTED_RETURNS
                ? filterOptions.filter(option => option.key !== 'returnStatus')
                : filterOptions
            }
            onChange={handleSelection}
            selected={selectedFilters}
            selectAll={deptSelectAll}
            pageName='returnStatus'
            showFilterCount
            closeOnOutsideClick
            isTranslated
          />

          {hasSelectedFilters && (
            <div className='order-status-item flex-container row' css={{ alignItems: 'center' }}>
              <div className='filters__clear body-4'>
                <Button
                  className='text-color-secondary body-4 p0-sm fl-sm-r  '
                  data-desc='gridwall__filter_clear'
                  onClick={() => dispatch(clearReturnsFilters())}
                >
                  <TranslatedCopy id='orderstatus.filters.clear' />
                </Button>
              </div>
            </div>
          )}
          <DateRangeSelector
            dateRange={dateRange}
            dateRangeOptions={dateRangeOptions}
            updateDateRange={dates => dispatch(updateReturnsDateRange(dates))}
            resetPage={() => null}
            filterList={() => null}
          />
          <div className='d-sm-h d-lg-b flex-expand'>
            <SortOptions
              list={returnList}
              sortBy={sortOptions}
              defaultValue={{
                value: DEFAULT_SORT.field,
                name: new TranslatedCopy({ id: 'returnStatus.orderSubmittedDate' }).toString(),
                desc: true
              }}
              handleExternalSort={handleListSort}
            />
          </div>
        </div>
        <div className='status__filters_bg' onClick={() => setIsMobileFiltersOpen(false)} />
        <div className='p5-sm'>
          {isFetching && <LoadingMessage title={new TranslatedCopy({ id: 'global.results' }).toString()} />}
          {!isFetching && returnList.length === 0 && (
            <NoResults titleId='returnStatus.search.advanced.noresults.title'>
              <div className='status__search_noResults'>
                <p className='mt5-sm mb3-sm'>
                  <TranslatedCopy id='returnStatus.search.advanced.noresults.copy' />
                </p>
              </div>
            </NoResults>
          )}
          {((returnList?.length > 0 && !isFetching) || (returnList?.length < totalResources && isFetching)) && (
            <>
              <div
                className='table-view body-4 pb4-md d-sm-h d-lg-b'
                css={css`
                  th:first-child {
                    box-shadow: inset 0px 1px 0px 0px #e5e5e5, inset 1px 0px 0px 0px #e5e5e5;
                  }
                  th:last-child,
                  td:last-child {
                    box-shadow: inset 0px 1px 0px 0px #e5e5e5, inset -1px 0px 0px 0px #e5e5e5;
                    background-color: unset;
                  }
                  td {
                    vertical-align: middle;
                  }
                `}
              >
                <TableInfiniteScroll
                  data={returnList}
                  columns={memoizedColumns}
                  loadMore={handleLoadNextPage}
                  hasMore={hasMoreReturnsToFetch}
                  clickTarget={null}
                  handleClick={handleReturnClick}
                  cellProps={{ handleDeleteClick }}
                  isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
                />
              </div>
              {/* FOR MOBILE */}
              <div className='d-lg-h' css={{ marginTop: '170px' }}>
                {returnList.map(returnStatus => (
                  <ReturnStatusCard
                    key={`returnCard__${returnStatus.returnId}`}
                    returnData={{
                      ...returnStatus,
                      departmentName: accountDepts?.find(
                        dept => dept.customerDepartmentCode === returnStatus.departmentNumber
                      )?.customerDepartmentName
                    }}
                    handleClick={handleReturnClick}
                    handleDeleteClick={handleDeleteClick}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnStatus;
